/**
 * FUNCTIONALITY-092 — Catalog Shell v2 UI adapter.
 * Normalizes Worker/R2 product cards into UI-safe objects.
 * Never treats 0/null as a sellable price.
 */

import type { ProductCardDTO } from "../api/types.js";

export type CatalogShellStockStatus =
  | "in_stock"
  | "low_stock"
  | "out_of_stock"
  | "unknown"
  | string;

export interface CatalogShellProductV2 {
  productId: string;
  sku: string;
  title: string;
  categoryId: string | null;
  categoryTitle: string | null;
  priceUah: number | null;
  stockStatus: CatalogShellStockStatus;
  imageUrl: string | null;
  isSellable: boolean;
  sellabilityReason: string | null;
  /** Original DTO for existing components */
  raw: ProductCardDTO;
}

function resolvePriceUah(product: ProductCardDTO): number | null {
  const raw =
    product.payable_price_uah ?? product.price_amount ?? product.price ?? null;
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function adaptCatalogProductV2(product: ProductCardDTO): CatalogShellProductV2 {
  const priceUah = resolvePriceUah(product);
  const stockStatus = String(product.stock_status ?? product.availability ?? "unknown");
  const workerSellable =
    (product as { is_sellable?: boolean | null }).is_sellable === true ||
    product.purchasable === true;
  const purchasable = workerSellable && priceUah != null;
  const reason =
    (product as { sellability_reason?: string | null }).sellability_reason ??
    product.unavailability_reason ??
    (!priceUah
      ? "unknown_price"
      : stockStatus === "out_of_stock"
        ? "out_of_stock"
        : purchasable
          ? null
          : "not_sellable");

  return {
    productId: String(product.product_id ?? product.id ?? "").trim(),
    sku: String(product.sku ?? product.product_id ?? "").trim(),
    title: String(product.title ?? "").trim() || String(product.product_id ?? ""),
    categoryId: String(product.category_id ?? product.category ?? "").trim() || null,
    categoryTitle: String(
      (product as { category_title?: string | null }).category_title ??
        (product as { category_path?: string | null }).category_path ??
        "",
    ).trim() || null,
    priceUah,
    stockStatus,
    imageUrl: product.image_url ?? null,
    isSellable: purchasable,
    sellabilityReason: reason,
    raw: {
      ...product,
      payable_price_uah: priceUah,
      price_amount: priceUah,
      price_resolved: priceUah != null,
      purchasable,
      unavailability_reason: reason,
    },
  };
}

export function adaptCatalogProductsV2(products: ProductCardDTO[]): CatalogShellProductV2[] {
  return products.map(adaptCatalogProductV2);
}
